import React, { FC } from 'react';

import type { HelpTypes, IOnboardingSentimentAnalysis } from '@pharmaplan/common';

import CollapsibleCard from '../../../../common/CollapsibleCard';
import ProfileContent from '../../ProfileContent/ProfileContent';
import strings from '../../../../../localization';
import {
  handleBooleanValue,
  handleValue,
} from '../../Pharmacy/PharmacyViewProfile/helper';
import useEditedFieldsDialog from '../../../../../hooks/Admin/Profile/useEditedFieldsDialog';

interface IOnboardingDetailsData {
  isAIOnboarding?: boolean | 0 | 1 | null;
  isLicenseVerified?: boolean | 0 | 1 | null
  sentimentAnalysis?: IOnboardingSentimentAnalysis | null;
  supportRequired?: boolean | 0 | 1 | null;
  conversationAudioUrl?: string | null;
  conversationTranscriptUrl?: string | null;
  editedProps?: string[] | null;
  userType?: HelpTypes | null;
}

interface IOnboardingDetailsProps {
  details?: IOnboardingDetailsData | null;
  successAction: string;
}

interface IOnboardingDetailsConfig {
  key: string;
  label: string;
  value: string | number;
  customOpen?: () => void;
  valueColor?: string;
}

const getLicenseVerificationConfig = (
  licenseVerified?: boolean | 0 | 1 | null,
): IOnboardingDetailsConfig => {
  const config = {
    key: 'licenseVerification',
    label: strings.licenseVerification,
  };

  if (licenseVerified === null || typeof licenseVerified === 'undefined') {
    return { ...config, value: `${handleValue(null)}` };
  }

  return licenseVerified
    ? { ...config, value: strings.licenseVerificationPass, valueColor: 'common.green600' }
    : { ...config, value: strings.licenseVerificationFail, valueColor: 'common.error' };
};

const getUserEmotionLabel = (userEmotion?: number | null) => {
  const userEmotionLabels: Record<number, string> = {
    1: strings.angry,
    2: strings.sarcastic,
    3: strings.irritated,
    4: strings.anxious,
    5: strings.confused,
    6: strings.irrelevant,
    7: strings.cheerful,
    8: strings.calm,
  };

  return userEmotionLabels[userEmotion ?? 0];
};

const isAiAssistedSignup = (isAiOnBoarding?: boolean | 0 | 1 | null) =>
  isAiOnBoarding === true || isAiOnBoarding === 1;

const getOptionalBooleanLabel = (
  value?: boolean | 0 | 1 | null,
) => {
  if (value === null || typeof value === 'undefined') {
    return handleValue(null);
  }

  return handleBooleanValue(value === true || value === 1);
};

const getConversationFileName = (conversationUrl?: string | null) => {
  if (!conversationUrl) {
    return null;
  }

  try {
    return new URL(conversationUrl).searchParams.get('fileName');
  } catch {
    return null;
  }
};

const getConversationFileConfig = (
  key: string,
  label: string,
  conversationUrl?: string | null,
): IOnboardingDetailsConfig | null => {
  const fileName = getConversationFileName(conversationUrl);

  if (!conversationUrl || !fileName) {
    return null;
  }

  return {
    key,
    label,
    value: fileName,
    customOpen: () =>
      window.open(conversationUrl, '_blank'),
  };
};

const getEditedFieldsConfig = (
  openEditedFields: (editedProps: string[]) => void,
  editedProps?: string[] | null,
): IOnboardingDetailsConfig => {
  const config = {
    key: 'fieldsEdited',
    label: strings.fieldsEdited,
  };

  if (!editedProps) {
    return { ...config, value: `${handleValue(null)}` };
  }

  const countConfig = { ...config, value: `${editedProps.length}` };

  if (editedProps.length === 0) {
    return countConfig;
  }

  return {
    ...countConfig,
    customOpen: () =>
      openEditedFields(editedProps),
  };
};

export const onboardingDetailsConfig = (
  details: IOnboardingDetailsData | null | undefined,
  openEditedFields: (editedProps: string[]) => void,
): Array<IOnboardingDetailsConfig> => {
  const {
    isAIOnboarding,
    sentimentAnalysis,
    conversationAudioUrl,
    conversationTranscriptUrl,
    editedProps,
    isLicenseVerified,
  } = details ?? {};

  const { userEmotion, supportRequired } = sentimentAnalysis ?? {};
  const isAiAssisted = isAiAssistedSignup(isAIOnboarding);

  const content: Array<IOnboardingDetailsConfig> = [
    {
      key: 'signUpType',
      label: strings.signUpType,
      value: isAiAssisted ? strings.aiAssisted : strings.manual,
    },
  ];

  if (!isAiAssisted) {
    return content;
  }

  const conversationFiles = [
    getConversationFileConfig(
      'recording',
      strings.recording,
      conversationAudioUrl,
    ),
    getConversationFileConfig(
      'transcript',
      strings.transcript,
      conversationTranscriptUrl,
    ),
  ].filter((file): file is IOnboardingDetailsConfig =>
    file !== null);

  return [
    ...content,
    {
      key: 'userEmotion',
      label: strings.userEmotion,
      value: handleValue(getUserEmotionLabel(userEmotion)),
    },
    {
      key: 'supportRequired',
      label: strings.supportRequired,
      value: getOptionalBooleanLabel(supportRequired),
    },
    getLicenseVerificationConfig(isLicenseVerified),
    getEditedFieldsConfig(openEditedFields, editedProps),
    ...conversationFiles,
  ];
};

const OnboardingDetails: FC<IOnboardingDetailsProps> = ({
  details,
  successAction,
}) => {
  const { handleOpenEditedFields } = useEditedFieldsDialog(details?.userType);

  return (
    <CollapsibleCard
      title={strings.onboardingDetails}
      keepExpanded
      content={(
        <ProfileContent
          successAction={successAction}
          content={onboardingDetailsConfig(details, handleOpenEditedFields)}
        />
      )}
    />
  );
};

export default OnboardingDetails;
